import { Fragment, useMemo } from 'react';
import { MapContainer, Marker, Polyline, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import type { LatLng, LobbyState } from '@geoguess/shared';

/**
 * Apple FindMy-style truth pin: green drop with white inner ring + dark dot.
 * SVG inlined into a divIcon so the pin has the iconic FindMy silhouette.
 */
const truthIcon = L.divIcon({
  html: `
    <svg viewBox="0 0 36 44" width="36" height="44" class="truth-pin">
      <defs>
        <linearGradient id="rt-pin-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#5be481"/>
          <stop offset="55%" stop-color="#30d158"/>
          <stop offset="100%" stop-color="#1ea846"/>
        </linearGradient>
      </defs>
      <path d="M18 1.5C9.44 1.5 2.5 8.44 2.5 17c0 10.4 12.7 22.2 14.78 24.07a1 1 0 0 0 1.34 0C20.7 39.2 33.5 27.4 33.5 17c0-8.56-6.94-15.5-15.5-15.5z"
        fill="url(#rt-pin-grad)" stroke="#ffffff" stroke-width="1.5"/>
      <circle cx="18" cy="17" r="7.5" fill="#ffffff"/>
      <circle cx="18" cy="17" r="2.6" fill="#1c1c1e"/>
    </svg>`,
  className: '',
  iconSize: [36, 44],
  iconAnchor: [18, 42],
});

function pinIcon(initial: string, color: string) {
  return L.divIcon({
    html: `<div class="guess-marker" style="background:${color}">${initial}</div>`,
    className: '',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

// iOS system-color palette (dark mode) for player guess pins.
const PALETTE = [
  '#0a84ff', // systemBlue
  '#ff9f0a', // systemOrange
  '#bf5af2', // systemPurple
  '#ff453a', // systemRed
  '#ffd60a', // systemYellow
  '#64d2ff', // systemTeal
  '#ff375f', // systemPink
  '#5e5ce6', // systemIndigo
];

export function RevealMap({ truth, lobby }: { truth: LatLng; lobby: LobbyState }) {
  const players = useMemo(() => Object.values(lobby.players), [lobby.players]);
  const center = useMemo(() => {
    const lats = [truth.lat, ...players.flatMap((p) => (p.lastGuess ? [p.lastGuess.lat] : []))];
    const lngs = [truth.lng, ...players.flatMap((p) => (p.lastGuess ? [p.lastGuess.lng] : []))];
    const cy = lats.reduce((a, b) => a + b, 0) / lats.length;
    const cx = lngs.reduce((a, b) => a + b, 0) / lngs.length;
    return [cy, cx] as [number, number];
  }, [players, truth]);

  return (
    <MapContainer
      center={center}
      zoom={2}
      worldCopyJump
      className="h-full w-full"
      attributionControl={false}
    >
      <TileLayer
        url={`${window.location.protocol}//${window.location.host}/carto-tiles/dark_all/{z}/{x}/{y}.png`}
        attribution='&copy; OSM &copy; CARTO'
      />
      <Marker position={[truth.lat, truth.lng]} icon={truthIcon} />
      {players.map((p, idx) => {
        if (!p.lastGuess) return null;
        const color = PALETTE[idx % PALETTE.length]!;
        return (
          <Fragment key={p.userId}>
            <Marker
              position={[p.lastGuess.lat, p.lastGuess.lng]}
              icon={pinIcon(p.name[0]?.toUpperCase() ?? '?', color)}
            />
            <Polyline
              positions={[
                [p.lastGuess.lat, p.lastGuess.lng],
                [truth.lat, truth.lng],
              ]}
              pathOptions={{ color, weight: 2, opacity: 0.75, dashArray: '4 6' }}
            />
          </Fragment>
        );
      })}
    </MapContainer>
  );
}
